import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  onAuthStateChanged,
  signInWithPhoneNumber,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  RecaptchaVerifier,
  ConfirmationResult,
  User
} from 'firebase/auth'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { auth } from '../libs/Firebase'
import { setCachedCustomClaims } from '../libs/ErrorHelper'
import {
  buildPortalUser,
  getEnrollmentUserIdFromClaims,
  hasSuperAdminRole,
  needsPortalClaimsSync,
  shouldHoldPortalAuthForViewAsHop,
  type PortalUser
} from '../libs/PortalAuthHelper'
import {
  clearPendingPortalLoginContext,
  clearPortalAuthErrorCode,
  consumePendingPortalEnrollmentUserId,
  consumePendingPortalEnrollmentUserIdByEmail,
  PortalAuthErrorCode,
  setPendingPortalLoginContextWithEmail,
  setPortalAuthErrorCode
} from '../libs/PortalLoginSessionHelper'
import { markPortalLoginPerf, setPortalLoginPerfSpan } from '../libs/PortalLoginPerfHelper'
import type { SetPortalUserClaimsRequest } from '../libs/CloudFunctionsApiHandler'
import {
  clearPendingPortalViewAsTicketId,
  clearPortalViewAsSession,
  getValidStoredPortalViewAsSession,
  readPendingPortalViewAsTicketId,
  readPortalViewAsSession,
  type PortalViewAsStoredSession,
  writePortalViewAsSession
} from '../libs/portalViewAsSession'

export const PORTAL_EMAIL_FOR_SIGN_IN_KEY = 'portal_email_for_sign_in'

export type { PortalUser }

interface PortalAuthContext {
  portalUser: PortalUser | null
  loading: boolean
  /** False while first-login custom claims sync runs in the background. */
  portalClaimsReady: boolean
  isViewAs: boolean
  viewAsExpiresAt: string | null
  /** School selected for this view-as ticket. Null means every school (unified portal). */
  viewAsClientId: string | null
  login: (phoneNumber: string, verificationCode: string, confirmationResult: ConfirmationResult) => Promise<void>
  logout: () => Promise<void>
  exitViewAs: () => Promise<void>
  activateViewAsSession: (session: PortalViewAsStoredSession) => void
  sendVerificationCode: (phoneNumber: string) => Promise<ConfirmationResult>
  sendEmailSignInLink: (email: string, enrollmentUserId: string) => Promise<void>
  completeEmailSignIn: (email: string) => Promise<void>
  /** Clears the reCAPTCHA container so the next SMS send renders a fresh widget. */
  requestRecaptchaReinit: () => void
}

const PortalAuthContext = createContext<PortalAuthContext | undefined>(undefined)

const syncPortalUserClaims = async (
  firebaseUser: User,
  claimsOptions?: { enrollmentUserId?: string }
): Promise<string | null> => {
  const { setPortalUserClaims } = await import('../libs/CloudFunctionsApiHandler')
  const request: SetPortalUserClaimsRequest = { uid: firebaseUser.uid }

  if (!firebaseUser.phoneNumber && firebaseUser.email && claimsOptions?.enrollmentUserId) {
    request.email = firebaseUser.email
    request.enrollmentUserId = claimsOptions.enrollmentUserId
  }

  await setPortalUserClaims(request)
  await firebaseUser.getIdToken(true)
  const idTokenResult = await firebaseUser.getIdTokenResult()
  setCachedCustomClaims(idTokenResult.claims as Record<string, unknown>)
  return getEnrollmentUserIdFromClaims(idTokenResult.claims as { portalUser?: boolean; enrollmentUserId?: string })
}

const RECAPTCHA_MOUNT_ID = 'recaptcha-mount'
const RECAPTCHA_CONTAINER_ID = 'recaptcha-container'

export const PortalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [portalClaimsReady, setPortalClaimsReady] = useState(false)
  const [isViewAs, setIsViewAs] = useState(false)
  const [viewAsExpiresAt, setViewAsExpiresAt] = useState<string | null>(null)
  const [viewAsClientId, setViewAsClientId] = useState<string | null>(null)
  const claimsSyncInFlightRef = useRef(false)
  const sendRecaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null)
  const exitingViewAsRef = useRef(false)

  const teardownRecaptcha = useCallback(() => {
    if (sendRecaptchaVerifierRef.current) {
      try {
        sendRecaptchaVerifierRef.current.clear()
      } catch {
        // ignore — verifier may already be cleared or single-use consumed
      }
      sendRecaptchaVerifierRef.current = null
    }
    const mount = document.getElementById(RECAPTCHA_MOUNT_ID)
    if (mount) {
      mount.innerHTML = ''
    }
  }, [])

  const buildRecaptchaVerifier = useCallback((): RecaptchaVerifier => {
    return new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
      size: 'invisible',
      callback: () => {
        console.log('✅ reCAPTCHA solved successfully')
      },
      'expired-callback': () => {
        console.warn('⚠️ reCAPTCHA expired, user needs to solve it again')
      },
      'error-callback': (error: unknown) => {
        console.error('❌ reCAPTCHA error:', error)
      }
    })
  }, [])

  const acquireRecaptchaVerifierForSend = useCallback(async (): Promise<RecaptchaVerifier> => {
    teardownRecaptcha()

    const mount = document.getElementById(RECAPTCHA_MOUNT_ID)
    if (!mount) {
      throw new Error('reCAPTCHA mount not found')
    }

    // Create a fresh container inside the React-owned mount so React never
    // reconciles away the reCAPTCHA widget children.
    const container = document.createElement('div')
    container.id = RECAPTCHA_CONTAINER_ID
    mount.appendChild(container)

    const verifier = buildRecaptchaVerifier()
    await verifier.render()
    sendRecaptchaVerifierRef.current = verifier
    return verifier
  }, [buildRecaptchaVerifier, teardownRecaptcha])

  const requestRecaptchaReinit = useCallback(() => {
    teardownRecaptcha()
  }, [teardownRecaptcha])

  const clearViewAsState = useCallback(() => {
    setIsViewAs(false)
    setViewAsExpiresAt(null)
    setViewAsClientId(null)
  }, [])

  const activateViewAsSession = useCallback((session: PortalViewAsStoredSession) => {
    const firebaseUser = auth.currentUser
    if (!firebaseUser) {
      return
    }
    writePortalViewAsSession(session)
    clearPendingPortalViewAsTicketId()
    setPortalUser(buildPortalUser(firebaseUser, session.enrollmentUserId))
    setIsViewAs(true)
    setViewAsExpiresAt(session.expiresAt)
    setViewAsClientId(session.clientId ?? null)
    setPortalClaimsReady(true)
  }, [])

  const exitViewAs = useCallback(async (): Promise<void> => {
    if (exitingViewAsRef.current) {
      return
    }
    exitingViewAsRef.current = true
    const stored = readPortalViewAsSession()
    const ticketId = stored?.ticketId
    clearPendingPortalViewAsTicketId()
    clearPortalViewAsSession()
    clearViewAsState()
    setPortalUser(null)
    setPortalClaimsReady(true)
    try {
      if (ticketId) {
        const { endPortalViewAs } = await import('../libs/CloudFunctionsApiHandler')
        await endPortalViewAs({ ticketId })
      }
    } catch {
      // Ticket may already be ended or expired.
    }
    try {
      await auth.signOut()
    } finally {
      exitingViewAsRef.current = false
      navigate(buildRoute(RoutePath.PORTAL_VIEW_AS_ENDED), { replace: true })
    }
  }, [clearViewAsState, navigate])

  const resolveSuperAdminViewAs = useCallback(
    async (firebaseUser: User): Promise<void> => {
      const stored = getValidStoredPortalViewAsSession()
      if (!stored) {
        if (readPendingPortalViewAsTicketId()) {
          return
        }
        clearPortalViewAsSession()
        setPortalUser(null)
        clearViewAsState()
        setPortalClaimsReady(true)
        return
      }

      try {
        const { getPortalViewAsSession } = await import('../libs/CloudFunctionsApiHandler')
        const session = await getPortalViewAsSession({ ticketId: stored.ticketId })
        writePortalViewAsSession({
          ticketId: session.ticketId,
          enrollmentUserId: session.enrollmentUserId,
          expiresAt: session.expiresAt,
          ...(session.clientId ? { clientId: session.clientId } : {})
        })
        setPortalUser(buildPortalUser(firebaseUser, session.enrollmentUserId))
        setIsViewAs(true)
        setViewAsExpiresAt(session.expiresAt)
        setViewAsClientId(session.clientId ?? null)
        setPortalClaimsReady(true)
      } catch {
        clearPortalViewAsSession()
        setPortalUser(null)
        clearViewAsState()
        setPortalClaimsReady(true)
      }
    },
    [clearViewAsState]
  )

  const runDeferredClaimsSync = useCallback(
    async (firebaseUser: User, claimsOptions?: { enrollmentUserId?: string }): Promise<void> => {
      if (claimsSyncInFlightRef.current) {
        return
      }
      claimsSyncInFlightRef.current = true
      const claimsStart = performance.now()

      try {
        const enrollmentUserId = await syncPortalUserClaims(firebaseUser, claimsOptions)
        setPortalLoginPerfSpan('setPortalUserClaimsMs', performance.now() - claimsStart)
        if (enrollmentUserId) {
          setPortalUser(buildPortalUser(firebaseUser, enrollmentUserId))
          setPortalClaimsReady(true)
          clearPendingPortalLoginContext()
        } else {
          console.error('[PortalAuthProvider] Missing enrollmentUserId after deferred claims sync, signing out')
          setPortalAuthErrorCode(PortalAuthErrorCode.ClaimsSyncFailed)
          setPortalUser(null)
          setPortalClaimsReady(false)
          clearPendingPortalLoginContext()
          await auth.signOut()
        }
      } catch (error) {
        console.error('Failed to set portal user claims:', error)
        setPortalAuthErrorCode(PortalAuthErrorCode.ClaimsSyncFailed)
        setPortalUser(null)
        setPortalClaimsReady(false)
        clearPendingPortalLoginContext()
        await auth.signOut()
      } finally {
        claimsSyncInFlightRef.current = false
      }
    },
    []
  )

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[PortalAuthProvider] Auth state changed:', !!firebaseUser, firebaseUser?.phoneNumber)

      if (firebaseUser) {
        const idTokenResult = await firebaseUser.getIdTokenResult()
        if (hasSuperAdminRole(idTokenResult.claims as { roles?: unknown })) {
          setCachedCustomClaims(idTokenResult.claims as Record<string, unknown>)
          await resolveSuperAdminViewAs(firebaseUser)
          setLoading(false)
          return
        }
        if (shouldHoldPortalAuthForViewAsHop(false, readPendingPortalViewAsTicketId())) {
          setLoading(false)
          return
        }
      }

      if (firebaseUser && firebaseUser.phoneNumber) {
        const authResolveStart = performance.now()
        markPortalLoginPerf('authStateResolveStart')

        const idTokenResult = await firebaseUser.getIdTokenResult()
        setCachedCustomClaims(idTokenResult.claims as Record<string, unknown>)

        const enrollmentUserIdFromClaims = getEnrollmentUserIdFromClaims(
          idTokenResult.claims as { portalUser?: boolean; enrollmentUserId?: string }
        )

        if (enrollmentUserIdFromClaims) {
          setPortalLoginPerfSpan('authStateResolveMs', performance.now() - authResolveStart)
          setPortalClaimsReady(true)
          clearPendingPortalLoginContext()
          console.log('[PortalAuthProvider] Setting portal user from token claims')
          setPortalUser(buildPortalUser(firebaseUser, enrollmentUserIdFromClaims))
        } else if (needsPortalClaimsSync(idTokenResult.claims as { portalUser?: boolean; enrollmentUserId?: string })) {
          const pendingEnrollmentUserId = consumePendingPortalEnrollmentUserId(firebaseUser.phoneNumber)
          if (pendingEnrollmentUserId) {
            setPortalLoginPerfSpan('authStateResolveMs', performance.now() - authResolveStart)
            setPortalClaimsReady(false)
            console.log(
              '[PortalAuthProvider] Setting portal user from pending enrollmentUserId; syncing claims in background'
            )
            setPortalUser(buildPortalUser(firebaseUser, pendingEnrollmentUserId))
            void runDeferredClaimsSync(firebaseUser)
          } else {
            const claimsStart = performance.now()
            try {
              const enrollmentUserId = await syncPortalUserClaims(firebaseUser)
              setPortalLoginPerfSpan('setPortalUserClaimsMs', performance.now() - claimsStart)
              if (enrollmentUserId) {
                setPortalLoginPerfSpan('authStateResolveMs', performance.now() - authResolveStart)
                setPortalClaimsReady(true)
                setPortalUser(buildPortalUser(firebaseUser, enrollmentUserId))
              } else {
                console.error('[PortalAuthProvider] Missing enrollmentUserId after claims sync, signing out')
                setPortalAuthErrorCode(PortalAuthErrorCode.ClaimsSyncFailed)
                setPortalUser(null)
                setPortalClaimsReady(false)
                await auth.signOut()
              }
            } catch (error) {
              console.error('Failed to set portal user claims:', error)
              setPortalAuthErrorCode(PortalAuthErrorCode.ClaimsSyncFailed)
              setPortalUser(null)
              setPortalClaimsReady(false)
              await auth.signOut()
            }
          }
        } else {
          console.error('[PortalAuthProvider] Missing enrollmentUserId in token claims, signing out')
          setPortalUser(null)
          setPortalClaimsReady(false)
          await auth.signOut()
        }
      } else if (firebaseUser && firebaseUser.email && !firebaseUser.phoneNumber) {
        const authResolveStart = performance.now()
        markPortalLoginPerf('authStateResolveStart')

        const idTokenResult = await firebaseUser.getIdTokenResult()
        setCachedCustomClaims(idTokenResult.claims as Record<string, unknown>)

        const enrollmentUserIdFromClaims = getEnrollmentUserIdFromClaims(
          idTokenResult.claims as { portalUser?: boolean; enrollmentUserId?: string }
        )

        if (enrollmentUserIdFromClaims) {
          setPortalLoginPerfSpan('authStateResolveMs', performance.now() - authResolveStart)
          setPortalClaimsReady(true)
          clearPendingPortalLoginContext()
          console.log('[PortalAuthProvider] Setting email-only portal user from token claims')
          setPortalUser(buildPortalUser(firebaseUser, enrollmentUserIdFromClaims))
        } else if (needsPortalClaimsSync(idTokenResult.claims as { portalUser?: boolean; enrollmentUserId?: string })) {
          const pendingEnrollmentUserId = consumePendingPortalEnrollmentUserIdByEmail(firebaseUser.email)
          if (pendingEnrollmentUserId) {
            setPortalLoginPerfSpan('authStateResolveMs', performance.now() - authResolveStart)
            setPortalClaimsReady(false)
            console.log(
              '[PortalAuthProvider] Setting email-only portal user from pending enrollmentUserId; syncing claims in background'
            )
            setPortalUser(buildPortalUser(firebaseUser, pendingEnrollmentUserId))
            void runDeferredClaimsSync(firebaseUser, { enrollmentUserId: pendingEnrollmentUserId })
          } else {
            // Cross-device first-login: no pending context on this device and no claims yet — user is signed out.
            console.error(
              '[PortalAuthProvider] Email-only user missing enrollmentUserId (no claims, no pending context), signing out'
            )
            setPortalAuthErrorCode(PortalAuthErrorCode.ClaimsSyncFailed)
            setPortalUser(null)
            setPortalClaimsReady(false)
            clearPendingPortalLoginContext()
            await auth.signOut()
          }
        } else {
          console.error('[PortalAuthProvider] Email-only user missing enrollmentUserId in token claims, signing out')
          setPortalAuthErrorCode(PortalAuthErrorCode.ClaimsSyncFailed)
          setPortalUser(null)
          setPortalClaimsReady(false)
          clearPendingPortalLoginContext()
          await auth.signOut()
        }
      } else {
        console.log('[PortalAuthProvider] No firebase user, clearing portal user')
        setPortalUser(null)
        setPortalClaimsReady(false)
        setCachedCustomClaims(null)
        // Do not clear pending login context here — email-link return needs enrollmentUserId
        // stashed when the link was sent (sessionStorage and/or localStorage).
      }
      setLoading(false)
    })

    return unsubscribe
  }, [resolveSuperAdminViewAs, runDeferredClaimsSync])

  const sendVerificationCode = async (phoneNumber: string): Promise<ConfirmationResult> => {
    try {
      console.log('🔐 Starting phone verification process...')
      console.log('📱 Phone number:', phoneNumber)

      const verifier = await acquireRecaptchaVerifierForSend()
      console.log('✅ reCAPTCHA verifier is ready')
      console.log('📤 Attempting to send SMS via Firebase Auth...')

      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier)

      console.log('✅ SMS sent successfully!')
      console.log('📨 Confirmation result received:', !!confirmationResult)

      teardownRecaptcha()

      return confirmationResult
    } catch (error: unknown) {
      teardownRecaptcha()

      const err = error as { code?: string; message?: string }
      console.error('❌ Error sending verification code:', error)
      console.error('🔍 Error code:', err?.code)
      console.error('🔍 Error message:', err?.message)

      if (err?.code === 'auth/invalid-app-credential') {
        console.error('❌ Firebase rejected the reCAPTCHA token (INVALID_APP_CREDENTIAL)')
        console.error('👉 Check Firebase Console → Authentication → Sign-in method → Phone')
        console.error(
          '👉 If reCAPTCHA Enterprise was enabled/disabled, reset via docs/firebase-recaptcha-reset-guide.md'
        )
      } else if (err?.code === 'auth/captcha-check-failed') {
        console.error('❌ reCAPTCHA verification failed')
        console.error('👉 This might be due to reCAPTCHA Enterprise configuration issues')
      } else if (err?.code === 'auth/invalid-phone-number') {
        console.error('❌ Invalid phone number format')
      } else if (err?.code === 'auth/quota-exceeded') {
        console.error('❌ SMS quota exceeded for this project')
      } else if (err?.code === 'auth/app-not-authorized') {
        console.error('❌ App not authorized for phone authentication')
      }

      throw error
    }
  }

  const login = async (
    phoneNumber: string,
    verificationCode: string,
    confirmationResult: ConfirmationResult
  ): Promise<void> => {
    try {
      await confirmationResult.confirm(verificationCode)
    } catch (error) {
      console.error('Error verifying code:', error)
      throw error
    }
  }

  const sendEmailSignInLink = async (email: string, enrollmentUserId: string): Promise<void> => {
    const actionCodeSettings = {
      url: `${window.location.origin}/portal/login?portalEmailLogin=1`,
      handleCodeInApp: true
    }

    await sendSignInLinkToEmail(auth, email, actionCodeSettings)
    setPendingPortalLoginContextWithEmail(enrollmentUserId, email)
    try {
      localStorage.setItem(PORTAL_EMAIL_FOR_SIGN_IN_KEY, email)
    } catch {
      // ignore quota / private mode errors
    }
  }

  const completeEmailSignIn = async (email: string): Promise<void> => {
    await signInWithEmailLink(auth, email, window.location.href)
  }

  const logout = async (): Promise<void> => {
    teardownRecaptcha()
    clearPendingPortalLoginContext()
    clearPortalAuthErrorCode()
    try {
      localStorage.removeItem(PORTAL_EMAIL_FOR_SIGN_IN_KEY)
    } catch {
      // ignore
    }
    if (isViewAs) {
      await exitViewAs()
      return
    }
    await auth.signOut()
    setPortalUser(null)
    setPortalClaimsReady(false)
    clearViewAsState()
  }

  useEffect(() => {
    if (!isViewAs || !viewAsExpiresAt) {
      return
    }
    const remainingMs = Date.parse(viewAsExpiresAt) - Date.now()
    if (remainingMs <= 0) {
      void exitViewAs()
      return
    }
    const timeoutId = window.setTimeout(() => {
      void exitViewAs()
    }, remainingMs)
    return () => window.clearTimeout(timeoutId)
  }, [exitViewAs, isViewAs, viewAsExpiresAt])

  return (
    <PortalAuthContext.Provider
      value={{
        portalUser,
        loading,
        portalClaimsReady,
        isViewAs,
        viewAsExpiresAt,
        viewAsClientId,
        login,
        logout,
        exitViewAs,
        activateViewAsSession,
        sendVerificationCode,
        sendEmailSignInLink,
        completeEmailSignIn,
        requestRecaptchaReinit
      }}
    >
      {children}
    </PortalAuthContext.Provider>
  )
}

export const usePortalAuth = (): PortalAuthContext => {
  const context = useContext(PortalAuthContext)
  if (!context) {
    throw new Error('usePortalAuth must be used within PortalAuthProvider')
  }
  return context
}
